// The island's MOTION LANGUAGE — one spring vocabulary the whole interactive
// layer speaks (owner directive 2026-11-03: "if bespoke means things don't POP
// and feel primitive, FIND THE POLISH"; scope-widen: one consistent motion
// language, not two systems fighting).
//
// This is the single place the island reaches for Motion One (`motion`, MIT,
// WAAPI-based, framework-free, tree-shakeable — adopted in docs/dependencies.md).
// Every other island module animates THROUGH here, so the field has ONE feel:
// the same spring constants, the same reduced-motion rule, the same "settle"
// character everywhere — the overlay's drag-release, the electric register's
// pulses, the stroke ink's settle, a fragment swapping in, a word reacting to a
// word. No module calls `el.animate` or `motion.animate` directly; they call
// these named springs so the vocabulary cannot drift.
//
// Canon 91 §1: framework-free. Motion One is a small animation primitive wired
// at the leaves, NOT an SPA framework — it owns no component model, no vdom, no
// router; it animates a DOM element's values and returns a handle. It baked into
// the one esbuild bundle (web/static/island.js); there is no second build step.
//
// prefers-reduced-motion is honoured HERE, once, for the whole island: every
// helper reads it live and, when set, jumps to the final value with no spring,
// no overshoot, no travelling — the end state, instantly. A module that animates
// through this module gets that for free and cannot forget it. (canon 94 §7 /
// canon 83: reduced motion is non-negotiable.)
//
// Lights, not cells (canon 83/94): these helpers move and light; they NEVER draw
// a border, a card, a fence or a cursor. Polish is motion QUALITY, not chrome.

import { animate } from "motion";

/** True when the viewer asked for reduced motion. Re-read live (not cached) so a
 *  system change mid-session is honoured on the next animation. The one reader
 *  for the whole island. */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/** A running animation the caller can cancel. Motion One's own handle is richer;
 *  this is the narrow surface the island depends on so the lib stays swappable. */
export interface MotionHandle {
  cancel(): void;
  /** Resolves when the animation finishes (or rejects on cancel — swallowed). */
  finished: Promise<void>;
}

const DONE: MotionHandle = { cancel() {}, finished: Promise.resolve() };

/** Wrap a Motion One animation in the island's narrow handle, swallowing the
 *  cancel rejection so a caller awaiting `.finished` never throws on teardown. */
function wrap(anim: { stop: () => void; finished: Promise<unknown> }): MotionHandle {
  return {
    cancel() {
      anim.stop();
    },
    finished: anim.finished.then(
      () => undefined,
      () => undefined,
    ),
  };
}

// ---- the field's spring vocabulary -----------------------------------------
//
// Four named springs, tuned for the field's register. They are the ONLY springs
// the island uses; a surface picks the one whose CHARACTER fits, never a raw
// stiffness/damping pair inline, so the whole field settles with one hand.

/** SETTLE — a drag/resize RELEASE coming to rest. Carries the release momentum
 *  into a gentle overshoot and settles: the pane keeps going a touch past the
 *  target, eases back, stops. Not a dead stop. Medium stiffness, light damping
 *  for a visible-but-restrained overshoot (canon: polish, never bounce-toy). */
export const SETTLE = { stiffness: 320, damping: 26, mass: 1 } as const;

/** RAISE — a bring-to-front POP. Tighter and quicker than SETTLE: the pane lifts
 *  with a small scale overshoot and settles fast, so raising reads as "this one
 *  came forward" without a slow wobble. */
export const RAISE = { stiffness: 520, damping: 30, mass: 1 } as const;

/** CHARGE — the active-pane / live-element charge swelling in. Soft and slow, a
 *  breath rather than a snap: the glow/scale eases up. Used where something
 *  becomes LIVE (the electric register, the active pane's charge). */
export const CHARGE = { stiffness: 180, damping: 24, mass: 1 } as const;

/** NUDGE — the smallest reaction: a word emphasising, a point brightening on
 *  proximity/focus. Crisp, almost no overshoot — a flick of life, not a move. */
export const NUDGE = { stiffness: 700, damping: 34, mass: 1 } as const;

export type SpringName = typeof SETTLE | typeof RAISE | typeof CHARGE | typeof NUDGE;

// ---- the helpers every island module animates through ----------------------

/**
 * Spring one element's values (transform, opacity, scale, box-shadow — anything
 * Motion One animates) to a target, with the field's named spring. Under reduced
 * motion the values are SET instantly with no animation. `velocity` seeds the
 * spring from a release speed (px/s for translate, so a flung pane carries its
 * momentum into the settle). Returns a handle.
 */
export function springTo(
  el: Element | Record<string, number | string>,
  to: Record<string, string | number>,
  sp: SpringName = SETTLE,
  opts: { velocity?: number; onUpdate?: () => void } = {},
): MotionHandle {
  // A plain carrier object (the overlay springs numbers it renders itself) vs a
  // DOM element (Motion animates its style). Both are valid Motion One targets.
  const isElement = typeof Element !== "undefined" && el instanceof Element;
  if (prefersReducedMotion()) {
    // The end state, instantly — no spring, no overshoot (canon 94 §7).
    const sink = isElement
      ? ((el as HTMLElement).style as unknown as Record<string, string>)
      : (el as Record<string, number | string>);
    for (const [k, v] of Object.entries(to)) {
      sink[k] = isElement && typeof v === "number" ? String(v) : (v as never);
    }
    opts.onUpdate?.();
    return DONE;
  }
  const transition: Record<string, unknown> = {
    type: "spring",
    ...sp,
  };
  if (typeof opts.velocity === "number") transition.velocity = opts.velocity;
  // `el` is a DOM element OR a plain numeric carrier (both valid Motion One
  // subjects); the overloads are typed per-subject, so the subject is passed as
  // `never` to select the runtime-correct path. `type: spring` passes Motion's
  // own spring generator as the transition's easing.
  const anim = animate(el as never, to as never, transition as never);
  if (opts.onUpdate) {
    // A cheap per-frame tick for callers that must mirror the animated value
    // elsewhere (the overlay writes a probe-readable transform string). Motion
    // drives the element; this only observes.
    const tick = () => {
      opts.onUpdate?.();
      if (!settled) requestAnimationFrame(tick);
    };
    let settled = false;
    void anim.finished.then(
      () => (settled = true),
      () => (settled = true),
    );
    requestAnimationFrame(tick);
  }
  return wrap(anim as never);
}

/**
 * A timed (non-spring) tween to a target with the field's easing — for fades and
 * the enter/exit of fragments where a spring would overshoot a fade. Reduced
 * motion sets the end state instantly. Duration in ms.
 */
export function easeTo(
  el: Element,
  to: Record<string, string | number>,
  durationMs = 240,
  easing: string | number[] = [0, 0, 0.3, 1], // --ease-out-3 (Open Props), the field's --ease-lift
): MotionHandle {
  if (prefersReducedMotion()) {
    const style = (el as HTMLElement).style;
    for (const [k, v] of Object.entries(to)) {
      (style as unknown as Record<string, string>)[k] = typeof v === "number" ? String(v) : v;
    }
    return DONE;
  }
  const anim = animate(el, to as never, { duration: durationMs / 1000, ease: easing } as never);
  return wrap(anim as never);
}

/**
 * A repeating "breathe" — a value easing up and back forever (the live charge's
 * cadence, canon 94 §7's 4.2s breath). Reduced motion leaves the element at its
 * resting value and animates nothing. Returns a handle whose cancel stops it.
 *
 * `delayMs` offsets the first cycle so two breathing elements can read as two
 * voices, not one (the electric register's per-side phase). `repeatType`
 * defaults to "mirror" (ease up and back — a breath); pass "loop" for a cycle
 * that restarts from the first keyframe each time (a travelling emission).
 */
export function breathe(
  el: Element,
  to: Record<string, string | number>,
  durationMs = 4200,
  opts: { delayMs?: number; repeatType?: "mirror" | "loop" | "reverse" } = {},
): MotionHandle {
  if (prefersReducedMotion()) return DONE;
  const anim = animate(el, to as never, {
    duration: durationMs / 1000,
    ease: "easeInOut",
    repeat: Infinity,
    repeatType: opts.repeatType ?? "mirror",
    ...(typeof opts.delayMs === "number" ? { delay: opts.delayMs / 1000 } : {}),
  } as never);
  return wrap(anim as never);
}

/**
 * A repeating keyframe LOOP — the field's perpetual-motion primitive. Where
 * `breathe` eases ONE value up and back, `loop` drives an explicit multi-stop
 * keyframe timeline forever (the shape of a travelling emission: out from the
 * voice, hold at the meeting point, fade; or a mark's pulse through named
 * scales). Every island surface that paints a NON-STOP animation (the electric
 * emission, the meeting-mark pulse, a rim answer arriving) goes through here, so
 * reduced motion is honoured in ONE place: under it the loop animates nothing
 * and returns the no-op handle, and the caller is expected to have already
 * placed the element in its resting/settled state (the lit-but-still frame).
 *
 * `keyframes` is a map of CSS property → array of stops (Motion One's keyframe
 * form); `offset` (0..1 fractional times) aligns the stops to a non-uniform
 * timeline. `ease` and `repeatType` default to the emission's character
 * (ease-in, loop); `delayMs` phase-offsets the start. Returns a cancel handle.
 */
export function loop(
  el: Element,
  keyframes: Record<string, Array<string | number>>,
  opts: {
    durationMs: number;
    delayMs?: number;
    offset?: number[];
    ease?: string | number[];
    repeatType?: "loop" | "mirror" | "reverse";
  },
): MotionHandle {
  if (prefersReducedMotion()) return DONE;
  const transition: Record<string, unknown> = {
    duration: opts.durationMs / 1000,
    repeat: Infinity,
    repeatType: opts.repeatType ?? "loop",
    ease: opts.ease ?? "easeIn",
  };
  if (typeof opts.delayMs === "number") transition.delay = opts.delayMs / 1000;
  if (opts.offset) transition.times = opts.offset;
  const anim = animate(el, keyframes as never, transition as never);
  return wrap(anim as never);
}

export { animate, spring, stagger } from "motion";
export { inView, press, hover } from "motion";
